import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Flag, CheckCircle2, Loader2, Bug, ShieldAlert, CreditCard, MoreHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import PageHeader from '../../components/settings/PageHeader';
import SettingCard from '../../components/settings/SettingCard';
import Toggle from '../../components/settings/Toggle';
import { settingsService } from '../../services/settings/settings.service';
import { validateRequired } from '../../utils/validators';
import type { ReportPayload } from '../../types/settings.types';
import { cn } from '../../utils/helpers';

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all text-sm';

const TYPES: { value: ReportPayload['type']; label: string; icon: LucideIcon }[] = [
  { value: 'bug', label: 'Bug', icon: Bug },
  { value: 'abuse', label: 'Abuse', icon: ShieldAlert },
  { value: 'payment', label: 'Payment', icon: CreditCard },
  { value: 'other', label: 'Other', icon: MoreHorizontal },
];

const SEVERITIES: ReportPayload['severity'][] = ['low', 'medium', 'high'];

export default function Report() {
  const [form, setForm] = useState<ReportPayload>({ type: 'bug', severity: 'medium', description: '', includeDiagnostics: true });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);

  const set = <K extends keyof ReportPayload>(key: K, value: ReportPayload[K]) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const err = validateRequired(form.description, 'Description');
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await settingsService.submitReport(form);
      setTicketId(res.ticketId);
    } catch {
      setError('Could not submit your report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (ticketId) {
    return (
      <div className="space-y-6 max-w-xl">
        <PageHeader title="Report a problem" icon={Flag} backTo="/app/settings/support" />
        <SettingCard>
          <div className="flex flex-col items-center text-center py-8">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4"><CheckCircle2 className="w-7 h-7 text-green-600" /></div>
            <h2 className="text-lg font-bold">Report submitted</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Thanks — our team will investigate.</p>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-3">Reference <span className="font-mono">{ticketId}</span></p>
            <Link to="/app/settings/support" className="mt-6 px-6 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 transition-colors">Back to Support</Link>
          </div>
        </SettingCard>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl">
      <PageHeader title="Report a problem" subtitle="Help us fix issues faster with a few details" icon={Flag} backTo="/app/settings/support" />

      <SettingCard>
        <form className="space-y-5" onSubmit={submit}>
          <div>
            <label className="text-sm font-medium text-neutral-700">What's this about?</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              {TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => set('type', t.value)}
                  className={cn('flex flex-col items-center gap-1.5 rounded-2xl border p-3 transition-all', form.type === t.value ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700')}
                >
                  <t.icon className="w-5 h-5" />
                  <span className="text-xs font-semibold">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700">Severity</label>
            <div className="flex gap-2 mt-2">
              {SEVERITIES.map((s) => (
                <button key={s} type="button" onClick={() => set('severity', s)} className={cn('flex-1 rounded-xl border py-2 text-sm font-semibold capitalize transition-all', form.severity === s ? 'border-neutral-900 bg-neutral-100 dark:bg-neutral-800' : 'border-neutral-200 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 hover:border-neutral-300 dark:hover:border-neutral-700')}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700">Description</label>
            <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={5} className={inputClass + ' mt-1 resize-none'} placeholder="What happened? Steps to reproduce help a lot." />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-2xl bg-neutral-50 dark:bg-neutral-950 px-4 py-3">
            <div>
              <p className="font-semibold text-sm">Include diagnostics</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Attach anonymized app logs to speed up triage</p>
            </div>
            <Toggle checked={form.includeDiagnostics} onChange={(v) => set('includeDiagnostics', v)} label="Include diagnostics" />
          </div>

          {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

          <button type="submit" disabled={submitting} className="w-full bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Submitting…' : 'Submit report'}
          </button>
        </form>
      </SettingCard>
    </div>
  );
}
